import React, { useState, useRef, useCallback } from "react";
import { Upload, Image as ImageIcon, Sparkles, Check, RefreshCw, X, ArrowRight, Palette, Copy, Zap, Shuffle } from "lucide-react";
import "./BrandThemeExtractor.css";

/* ══════════════ COLOR HELPERS ══════════════ */
const rgbToHsl = (r, g, b) => {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  if (max === min) { h = s = 0; }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: h = 0;
    }
    h /= 6;
  }
  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
};

const rgbToHex = (r, g, b) =>
  "#" + [r, g, b].map(x => Math.round(Math.min(255, Math.max(0, x))).toString(16).padStart(2, "0")).join("").toUpperCase();

const getLuminance = (r, g, b) => {
  const a = [r, g, b].map(v => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
};

const contrastRatio = (l1, l2) => {
  const lighter = Math.max(l1, l2), darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};

const hslToHex = (h, s, l) => {
  h = ((h % 360) + 360) % 360;
  s = Math.min(100, Math.max(0, s));
  l = Math.min(100, Math.max(0, l));
  const sn = s / 100, ln = l / 100;
  const k = n => (n + h / 30) % 12;
  const a = sn * Math.min(ln, 1 - ln);
  const f = n => ln - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return rgbToHex(Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255));
};

/* ══════════════ SMART COLOR QUANTIZER ══════════════ */
const quantizeColors = (imageData, width, height) => {
  const data = imageData.data;
  const step = Math.max(1, Math.floor((width * height) / 15000));
  const colorBuckets = {};
  let totalLuminance = 0, totalPixels = 0;
  let veryLightCount = 0, veryDarkCount = 0, coloredCount = 0;

  for (let i = 0; i < data.length; i += 4 * step) {
    const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
    if (a < 128) continue;
    const lum = getLuminance(r, g, b);
    const [, sat, lig] = rgbToHsl(r, g, b);
    totalLuminance += lum; totalPixels++;
    if (lum > 0.85) veryLightCount++;
    if (lum < 0.08) veryDarkCount++;
    if (sat > 25 && lig > 15 && lig < 85) coloredCount++;
    const qR = Math.round(r / 24) * 24;
    const qG = Math.round(g / 24) * 24;
    const qB = Math.round(b / 24) * 24;
    const key = `${qR},${qG},${qB}`;
    if (!colorBuckets[key]) colorBuckets[key] = { r: qR, g: qG, b: qB, count: 0 };
    colorBuckets[key].count++;
  }

  const sorted = Object.values(colorBuckets)
    .sort((a, b) => b.count - a.count)
    .map(c => {
      const [h, s, l] = rgbToHsl(c.r, c.g, c.b);
      return { ...c, hex: rgbToHex(c.r, c.g, c.b), lum: getLuminance(c.r, c.g, c.b), h, s, l };
    });

  const avgLuminance = totalPixels > 0 ? totalLuminance / totalPixels : 0.5;
  return {
    sorted,
    avgLuminance,
    lightRatio:  veryLightCount / totalPixels,
    darkRatio:   veryDarkCount  / totalPixels,
    colorRatio:  coloredCount   / totalPixels,
    totalPixels,
  };
};

/* ══════════════ MAIN THEME BUILDER ══════════════ */
const buildTheme = ({ sorted, avgLuminance, lightRatio, darkRatio, colorRatio }, fileName) => {
  const isLightBrand = lightRatio > 0.35 || avgLuminance > 0.55;
  const isDarkBrand  = darkRatio  > 0.25 || avgLuminance < 0.30;

  const bgCandidates = isLightBrand
    ? sorted.filter(c => c.lum > 0.7)
    : sorted.filter(c => c.lum < 0.25);
  const bgColor = bgCandidates[0] || sorted[0];
  const bgLum   = bgColor ? getLuminance(bgColor.r, bgColor.g, bgColor.b) : 0.5;

  const accent = sorted
    .filter(c => c.s > 35 && c.l > 20 && c.l < 80 && contrastRatio(c.lum, bgLum) > 2.5)
    .sort((a, b) => b.s - a.s)[0]
    || sorted.find(c => c.s > 20)
    || { r: 212, g: 175, b: 55, hex: "#D4AF37", h: 45, s: 65, l: 52, lum: 0.42 };

  const primaryCandidate = sorted.find(c => {
    const d = Math.min(Math.abs(c.h - accent.h), 360 - Math.abs(c.h - accent.h));
    return c.s > 20 && c.l > 15 && c.l < 85 && d > 30 && contrastRatio(c.lum, bgLum) > 2;
  });

  let bg, surface, text, textMuted, accentHex, borderHex, primary, brandStyle, fontPairId;

  if (isLightBrand && !isDarkBrand) {
    bg       = bgColor?.hex || "#FFFFFF";
    surface  = hslToHex(accent.h, Math.min(accent.s * 0.15, 12), 96);
    text     = "#0F172A";
    textMuted = "#64748B";
    accentHex = accent.hex;
    borderHex = hslToHex(accent.h, Math.min(accent.s * 0.2, 15), 88);
    primary  = primaryCandidate?.hex || hslToHex(accent.h, accent.s * 0.8, Math.max(15, accent.l - 20));
    if      (accent.h >= 35 && accent.h <= 55 && accent.s > 45) { brandStyle = "Champagne Gold Ivory"; fontPairId = "serif-elegant"; }
    else if (accent.h >= 190 && accent.h <= 240)                  { brandStyle = "Azure Corporate";      fontPairId = "modern-clean";  }
    else if (accent.h >= 130 && accent.h <= 165)                  { brandStyle = "Fresh Emerald";         fontPairId = "editorial";     }
    else if (accent.h >= 280 && accent.h <= 320)                  { brandStyle = "Luxe Violet";           fontPairId = "luxury-classic"; }
    else                                                           { brandStyle = "Bright Minimal";        fontPairId = "modern-clean";  }
  } else if (isDarkBrand && !isLightBrand) {
    const bgR = bgColor?.r ?? 15, bgG = bgColor?.g ?? 23, bgB = bgColor?.b ?? 42;
    bg        = bgColor?.hex || "#0F172A";
    surface   = rgbToHex(Math.min(255, bgR + 20), Math.min(255, bgG + 18), Math.min(255, bgB + 25));
    text      = "#F8FAFC";
    textMuted = "#94A3B8";
    accentHex = accent.hex;
    borderHex = rgbToHex(Math.min(255, bgR + 40), Math.min(255, bgG + 38), Math.min(255, bgB + 45));
    primary   = accent.lum > 0.6 ? accent.hex : hslToHex(accent.h, Math.min(accent.s * 0.9, 80), Math.min(85, accent.l + 25));
    if      (accent.h >= 35 && accent.h <= 58 && accent.s > 40) { brandStyle = "Royal Dark Gold";    fontPairId = "serif-elegant";  }
    else if (accent.h >= 190 && accent.h <= 240)                  { brandStyle = "Midnight Sapphire"; fontPairId = "luxury-classic"; }
    else if (accent.h >= 130 && accent.h <= 165)                  { brandStyle = "Deep Emerald";      fontPairId = "editorial";      }
    else if (accent.h >= 280 && accent.h <= 320)                  { brandStyle = "Dark Amethyst";     fontPairId = "luxury-classic"; }
    else if (accent.h <= 20  || accent.h >= 345)                  { brandStyle = "Crimson Dark";      fontPairId = "editorial";      }
    else                                                           { brandStyle = "Classic Dark";      fontPairId = "modern-clean";   }
  } else {
    bg        = "#FFFFFF";
    surface   = hslToHex(accent.h, 20, 96);
    text      = "#0F172A";
    textMuted = "#64748B";
    accentHex = accent.hex;
    borderHex = hslToHex(accent.h, 15, 88);
    primary   = primaryCandidate?.hex || accent.hex;
    brandStyle = "Vivid Brand Studio"; fontPairId = "modern-clean";
  }

  const baseName  = fileName.replace(/\.[^/.]+$/, "");
  const shortName = baseName.length > 18 ? baseName.slice(0, 16) + "\u2026" : baseName;

  return {
    id: "brand-extracted-" + Date.now(),
    name: `${brandStyle} \u2014 ${shortName}`,
    brandStyle,
    accentHue:  accent.h,
    accentSat:  accent.s,
    isLightBrand,
    isDarkBrand,
    catalogueTheme: isLightBrand ? "pearl" : "midnight",
    fontPairId,
    isExtracted: true,
    extractedFrom: fileName,
    colors: { bg, surface, text, textMuted, accent: accentHex, border: borderHex, primary },
  };
};

/* ══════════════ RANDOM VARIATION GENERATOR ══════════════
   Takes the extracted accent hue+sat as seed and generates
   6 logical, distinct theme variations — NOT saved to storage.
 ══════════════════════════════════════════════════════════ */
const VARIATION_RECIPES = [
  { label: "Complementary Dark",   hueDelta: 180, dark: true,  satMult: 1.0, desc: "Opposite hue, dark studio"    },
  { label: "Analogous Light",      hueDelta: 30,  dark: false, satMult: 0.9, desc: "+30° hue, clean ivory"        },
  { label: "Triadic",              hueDelta: 120, dark: true,  satMult: 1.1, desc: "120° triadic, luxe dark"      },
  { label: "Split-Complement",     hueDelta: 150, dark: false, satMult: 0.8, desc: "Split complement, minimal"    },
  { label: "Dark Mode Same Hue",   hueDelta: 0,   dark: true,  satMult: 0.7, desc: "Same hue, deep dark mode"    },
  { label: "Light Mode Same Hue",  hueDelta: 0,   dark: false, satMult: 0.75,desc: "Same hue, bright ivory"       },
  { label: "Warm Tint",            hueDelta: -20, dark: false, satMult: 1.2, desc: "Warm tinted, clean light"     },
  { label: "Cool Deep",            hueDelta: 200, dark: true,  satMult: 1.0, desc: "Cool complementary, dark"     },
];

const generateVariation = (baseHue, baseSat, dark, hueDelta, satMult) => {
  const h  = ((baseHue + hueDelta) % 360 + 360) % 360;
  const s  = Math.min(90, Math.max(20, baseSat * satMult));
  const accentL = dark ? 58 : 48;

  if (dark) {
    return {
      bg:       hslToHex(h, Math.min(s * 0.25, 22), 7),
      surface:  hslToHex(h, Math.min(s * 0.3,  25), 13),
      text:     hslToHex(h, Math.min(s * 0.15, 12), 92),
      textMuted:hslToHex(h, Math.min(s * 0.1,  8),  58),
      accent:   hslToHex(h, s, accentL),
      border:   hslToHex(h, Math.min(s * 0.3,  20), 22),
      primary:  hslToHex(h, s * 0.9, Math.min(accentL + 22, 85)),
    };
  } else {
    return {
      bg:       "#FFFFFF",
      surface:  hslToHex(h, Math.min(s * 0.12, 10), 97),
      text:     hslToHex(h, Math.min(s * 0.35, 30), 10),
      textMuted:hslToHex(h, Math.min(s * 0.2,  15), 45),
      accent:   hslToHex(h, s, accentL),
      border:   hslToHex(h, Math.min(s * 0.15, 12), 88),
      primary:  hslToHex(h, s * 0.85, Math.max(12, accentL - 18)),
    };
  }
};

const buildVariations = (extractedTheme) => {
  const { accentHue = 45, accentSat = 65 } = extractedTheme;
  return VARIATION_RECIPES.map((recipe, i) => {
    const colors = generateVariation(accentHue, accentSat, recipe.dark, recipe.hueDelta, recipe.satMult);
    return {
      id:          `preview-variation-${i}`,
      label:       recipe.label,
      desc:        recipe.desc,
      dark:        recipe.dark,
      colors,
      // NOT saved — just a transient preview object
    };
  });
};

/* ══════════════ COMPONENT ══════════════ */
const BrandThemeExtractor = ({ onClose, onApplyTheme }) => {
  const [imageSrc,        setImageSrc]        = useState(null);
  const [imageName,       setImageName]       = useState("");
  const [isProcessing,    setIsProcessing]    = useState(false);
  const [progressStage,   setProgressStage]   = useState("");
  const [progressPct,     setProgressPct]     = useState(0);
  const [dragOver,        setDragOver]        = useState(false);
  const [extractedTheme,  setExtractedTheme]  = useState(null);
  const [copiedToken,     setCopiedToken]     = useState(false);

  // Variation generator state
  const [variations,      setVariations]      = useState([]);
  const [showVariations,  setShowVariations]  = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null); // preview-only, not saved

  const fileInputRef = useRef(null);
  const canvasRef    = useRef(null);

  const STAGES = [
    "Reading file\u2026",
    "Resizing image on canvas\u2026",
    "Sampling pixel colours\u2026",
    "Analysing brand luminance\u2026",
    "Building smart palette\u2026",
    "Generating design tokens\u2026",
    "Finalising theme\u2026",
  ];

  const processImage = useCallback((file) => {
    if (!file || !file.type.match(/^image\/(png|jpe?g|webp)$/i)) return;
    setImageName(file.name);
    setExtractedTheme(null);
    setVariations([]);
    setShowVariations(false);
    setSelectedVariant(null);
    setIsProcessing(true);
    setProgressPct(0);
    setProgressStage(STAGES[0]);

    const reader = new FileReader();
    reader.onload = (e) => {
      const src = e.target.result;
      setImageSrc(src);
      setProgressPct(14); setProgressStage(STAGES[1]);

      const img = new Image();
      img.onload = () => {
        setProgressPct(28); setProgressStage(STAGES[2]);
        const MAX = 900;
        let w = img.width, h = img.height;
        if (w > MAX || h > MAX) {
          if (w > h) { h = Math.round((h * MAX) / w); w = MAX; }
          else       { w = Math.round((w * MAX) / h); h = MAX; }
        }
        const canvas = canvasRef.current || document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);

        setTimeout(() => { setProgressStage(STAGES[3]); setProgressPct(45); }, 100);
        setTimeout(() => { setProgressStage(STAGES[4]); setProgressPct(62); }, 250);
        setTimeout(() => { setProgressStage(STAGES[5]); setProgressPct(78); }, 400);

        setTimeout(() => {
          try {
            const imageData = ctx.getImageData(0, 0, w, h);
            const analysis  = quantizeColors(imageData, w, h);
            setProgressStage(STAGES[6]); setProgressPct(92);
            const theme = buildTheme(analysis, file.name);
            setTimeout(() => {
              setExtractedTheme(theme);
              setProgressPct(100);
              setIsProcessing(false);
            }, 200);
          } catch (err) {
            console.error("Extraction error:", err);
            setIsProcessing(false);
          }
        }, 550);
      };
      img.src = src;
    };
    reader.readAsDataURL(file);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Generate fresh variations from extracted palette ── */
  const handleGenerateVariations = () => {
    if (!extractedTheme) return;
    const vars = buildVariations(extractedTheme);
    setVariations(vars);
    setShowVariations(true);
    setSelectedVariant(null);
  };

  /* ── Shuffle: re-generate using random hue spin per recipe ── */
  const handleShuffle = () => {
    if (!extractedTheme) return;
    const spin = Math.floor(Math.random() * 60) - 30; // ±30° random drift
    const baseHue = ((extractedTheme.accentHue + spin) % 360 + 360) % 360;
    const baseSat = Math.min(90, Math.max(25, extractedTheme.accentSat + (Math.random() * 20 - 10)));
    const vars = VARIATION_RECIPES.map((recipe, i) => {
      const colors = generateVariation(baseHue, baseSat, recipe.dark, recipe.hueDelta, recipe.satMult);
      return { id: `preview-variation-${i}`, label: recipe.label, desc: recipe.desc, dark: recipe.dark, colors };
    });
    setVariations(vars);
    setSelectedVariant(null);
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0]; if (f) processImage(f);
  };
  const handleFileSelect = (e) => {
    const f = e.target.files?.[0]; if (f) processImage(f);
  };

  const copyCssVars = () => {
    if (!extractedTheme) return;
    const { colors } = extractedTheme;
    const css = "/* Brand Theme: " + extractedTheme.name + " */\n:root {\n" +
      Object.entries(colors).map(([k, v]) => "  --theme-" + k + ": " + v + ";").join("\n") + "\n}";
    navigator.clipboard.writeText(css);
    setCopiedToken(true);
    setTimeout(() => setCopiedToken(false), 2200);
  };

  /* ── Apply: either the extracted theme or a selected variation ──
     Variations are NOT saved to localStorage — they are transient.
     We pass isPreviewOnly flag so VisualDesignerStudio knows not to persist. */
  const handleApply = () => {
    if (selectedVariant) {
      // Merge variation colours into a theme-like object tagged as preview-only
      const preview = {
        id:             "preview-" + Date.now(),
        name:           selectedVariant.label + " (Preview)",
        brandStyle:     selectedVariant.label,
        catalogueTheme: selectedVariant.dark ? "midnight" : "pearl",
        fontPairId:     extractedTheme.fontPairId,
        isExtracted:    true,
        isPreviewOnly:  true,   // ← signals: do NOT save to localStorage
        colors:         selectedVariant.colors,
      };
      onApplyTheme(preview);
    } else if (extractedTheme) {
      onApplyTheme(extractedTheme);
    }
  };

  const activePreview = selectedVariant
    ? { ...extractedTheme, name: selectedVariant.label, colors: selectedVariant.colors }
    : extractedTheme;

  const SWATCH_KEYS = [
    { key: "bg",        label: "Background" },
    { key: "surface",   label: "Surface"    },
    { key: "accent",    label: "Accent"     },
    { key: "primary",   label: "Primary"    },
    { key: "text",      label: "Text"       },
    { key: "textMuted", label: "Muted"      },
    { key: "border",    label: "Border"     },
  ];

  return (
    <div className="brand-theme-extractor-overlay" onClick={onClose}>
      <div className="brand-theme-extractor-modal" onClick={e => e.stopPropagation()}>
        <canvas ref={canvasRef} style={{ display: "none" }} />

        {/* ─── Header ─── */}
        <div className="extractor-header">
          <div className="extractor-title">
            <div className="extractor-title-icon"><Sparkles size={18} /></div>
            <div>
              <h3>Brand Theme Extractor</h3>
              <p>Upload a visiting card, logo or letterhead — we extract your palette automatically</p>
            </div>
          </div>
          <button type="button" className="close-btn" onClick={onClose}><X size={20} /></button>
        </div>

        {/* ─── Body ─── */}
        <div className="extractor-body">

          {/* Upload Zone */}
          <div
            className={"dropzone" + (dragOver ? " dragover" : "") + (imageSrc ? " has-image" : "")}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input type="file" ref={fileInputRef} accept="image/png,image/jpeg,image/webp"
              onChange={handleFileSelect} style={{ display: "none" }} />

            {imageSrc ? (
              <div className="uploaded-preview-container">
                <div className="uploaded-preview-frame">
                  <img src={imageSrc} alt="Brand asset" className="uploaded-preview-img" />
                </div>
                <div className="uploaded-preview-meta">
                  <div className="file-name-row"><ImageIcon size={14} /><span className="file-name">{imageName}</span></div>
                  <p className="file-hint">Click to replace</p>
                  <button type="button" className="reupload-btn"
                    onClick={e => { e.stopPropagation(); fileInputRef.current?.click(); }}>
                    <RefreshCw size={13} /> Replace Asset
                  </button>
                </div>
              </div>
            ) : (
              <div className="dropzone-content">
                <div className="upload-icon-circle"><Upload size={26} /></div>
                <h4>Drag &amp; Drop Brand Image</h4>
                <p>Visiting Cards &middot; Logos &middot; Letterheads</p>
                <p className="file-types">PNG &middot; JPG &middot; WEBP</p>
                <span className="browse-pill"><Zap size={12} />&nbsp;Browse &amp; Extract</span>
              </div>
            )}
          </div>

          {/* Progress Bar */}
          {isProcessing && (
            <div className="extractor-progress">
              <div className="progress-bar-track">
                <div className="progress-bar-fill" style={{ width: progressPct + "%" }} />
              </div>
              <div className="progress-status-row">
                <div className="progress-spinner" />
                <span>{progressStage}</span>
                <span className="progress-pct">{progressPct}%</span>
              </div>
            </div>
          )}

          {/* Results */}
          {extractedTheme && !isProcessing && (
            <div className="extractor-results">

              {/* Brand Style Header */}
              <div className="results-header">
                <div className="brand-style-tag">
                  <Palette size={15} />
                  <div>
                    <span className="brand-style-label">Detected Brand Style</span>
                    <strong className="brand-style-name">{extractedTheme.brandStyle}</strong>
                  </div>
                </div>
                <button type="button" className="copy-vars-btn" onClick={copyCssVars}>
                  {copiedToken ? <Check size={13} color="#22c55e" /> : <Copy size={13} />}
                  <span>{copiedToken ? "Copied!" : "Copy CSS"}</span>
                </button>
              </div>

              {/* Swatch Row — from currently selected preview or extracted */}
              <div className="swatch-section">
                <p className="swatch-section-title">
                  {selectedVariant ? `Variation Preview: ${selectedVariant.label}` : "Extracted Color Tokens"}
                  {selectedVariant && <span className="preview-only-badge">Preview Only</span>}
                </p>
                <div className="swatch-grid">
                  {SWATCH_KEYS.map(({ key, label }) => (
                    <div key={key} className="swatch-card">
                      <div className="swatch-color-box" style={{ backgroundColor: (activePreview?.colors || extractedTheme.colors)[key] }}>
                        <span className="swatch-hex-overlay">{(activePreview?.colors || extractedTheme.colors)[key]}</span>
                      </div>
                      <span className="swatch-label">{label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* ══ RANDOM VARIATION GENERATOR ══ */}
              <div className="variation-generator-section">
                <div className="variation-header">
                  <div className="variation-header-left">
                    <Shuffle size={15} />
                    <div>
                      <span className="variation-title">Random Theme Variations</span>
                      <span className="variation-subtitle">Generated from your extracted brand hue · Not saved</span>
                    </div>
                  </div>
                  <div className="variation-actions">
                    {showVariations && (
                      <button type="button" className="shuffle-btn" onClick={handleShuffle} title="Shuffle again">
                        <RefreshCw size={13} /> Shuffle
                      </button>
                    )}
                    <button type="button" className="generate-variations-btn" onClick={handleGenerateVariations}>
                      <Sparkles size={13} />
                      {showVariations ? "Regenerate" : "Generate Variations"}
                    </button>
                  </div>
                </div>

                {showVariations && (
                  <div className="variations-grid">
                    {variations.map((v) => {
                      const isSelected = selectedVariant?.id === v.id;
                      return (
                        <button
                          key={v.id}
                          type="button"
                          className={"variation-card" + (isSelected ? " selected" : "")}
                          onClick={() => setSelectedVariant(isSelected ? null : v)}
                          title={v.desc}
                        >
                          {/* Mini Palette Strip */}
                          <div className="variation-palette-strip">
                            <div style={{ background: v.colors.bg,      flex: 2 }} />
                            <div style={{ background: v.colors.accent,  flex: 1 }} />
                            <div style={{ background: v.colors.surface, flex: 1 }} />
                            <div style={{ background: v.colors.primary, flex: 1 }} />
                          </div>
                          {/* Label */}
                          <div className="variation-card-body" style={{
                            backgroundColor: v.colors.bg,
                            borderColor: isSelected ? v.colors.accent : "transparent",
                          }}>
                            <span className="variation-label" style={{ color: v.colors.text }}>{v.label}</span>
                            <span className="variation-desc"  style={{ color: v.colors.textMuted }}>{v.desc}</span>
                            {isSelected && (
                              <span className="variation-selected-badge" style={{
                                backgroundColor: v.colors.accent,
                                color: v.colors.bg,
                              }}>Selected</span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {!showVariations && (
                  <p className="variation-hint">
                    Click <strong>Generate Variations</strong> to create 8 logical palette variations derived from your brand hue. These are preview-only and won't be saved unless you apply them.
                  </p>
                )}
              </div>

              {/* Live Poster Card Preview */}
              <div className="extracted-live-preview">
                <p className="swatch-section-title">
                  Live Poster Card Preview
                  {selectedVariant && <span className="preview-only-badge">Variation Preview</span>}
                </p>
                <div className="preview-poster-card" style={{
                  backgroundColor: (activePreview?.colors || extractedTheme.colors).bg,
                  borderColor:     (activePreview?.colors || extractedTheme.colors).accent,
                }}>
                  <div className="preview-top-bar" style={{
                    backgroundColor: (activePreview?.colors || extractedTheme.colors).surface,
                    borderBottomColor:(activePreview?.colors || extractedTheme.colors).border,
                  }}>
                    <div className="preview-logo-placeholder" style={{
                      backgroundColor: (activePreview?.colors || extractedTheme.colors).accent,
                      color:           (activePreview?.colors || extractedTheme.colors).bg,
                    }}>JMI</div>
                    <span className="preview-brand-name" style={{ color: (activePreview?.colors || extractedTheme.colors).text }}>
                      YOUR JEWELLERY STORE
                    </span>
                    <span className="preview-badge" style={{
                      backgroundColor: (activePreview?.colors || extractedTheme.colors).accent,
                      color:           (activePreview?.colors || extractedTheme.colors).bg,
                    }}>BRAND MATCH</span>
                  </div>
                  <div className="preview-card-body">
                    <div className="preview-img-box" style={{
                      backgroundColor: (activePreview?.colors || extractedTheme.colors).surface,
                      borderColor:     (activePreview?.colors || extractedTheme.colors).border,
                    }}>
                      <ImageIcon size={36} style={{ color: (activePreview?.colors || extractedTheme.colors).accent }} />
                    </div>
                    <div className="preview-card-details">
                      <span className="preview-karat" style={{
                        backgroundColor: (activePreview?.colors || extractedTheme.colors).accent,
                        color:           (activePreview?.colors || extractedTheme.colors).bg,
                      }}>22K HALLMARKED</span>
                      <span className="preview-title" style={{ color: (activePreview?.colors || extractedTheme.colors).text }}>
                        Gold Bridal Necklace Set
                      </span>
                      <span className="preview-sub" style={{ color: (activePreview?.colors || extractedTheme.colors).textMuted }}>
                        Gross: 42.5g &middot; Net: 38.2g
                      </span>
                      <div className="preview-price-row">
                        <span className="preview-price" style={{
                          backgroundColor: (activePreview?.colors || extractedTheme.colors).surface,
                          color:           (activePreview?.colors || extractedTheme.colors).accent,
                          borderColor:     (activePreview?.colors || extractedTheme.colors).border,
                        }}>&#8377;4,25,000</span>
                        <span className="preview-price-muted" style={{ color: (activePreview?.colors || extractedTheme.colors).textMuted }}>MRP</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          )}
        </div>

        {/* ─── Footer ─── */}
        <div className="extractor-footer">
          {selectedVariant && (
            <span className="footer-preview-note">
              Applying: <strong>{selectedVariant.label}</strong> (not saved)
            </span>
          )}
          <button type="button" className="cancel-btn" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="apply-btn"
            disabled={!extractedTheme || isProcessing}
            onClick={handleApply}
          >
            <Sparkles size={15} />
            {selectedVariant ? "Apply Variation" : "Apply Brand Theme"}
            <ArrowRight size={15} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default BrandThemeExtractor;
